import { FC, useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { CreditCard, Banknote, Search, Smartphone, Bike } from 'lucide-react';

import { CheckoutModalProps } from './CheckoutModal.types';
import { posService } from '@/services/pos.service';
import type { ReceiptType } from '../../types/pos.types';
import { NumericKeypad, DecimalKeypad, TextKeypad, DNIKeypad } from '@/components/ui/keypads';

export const CheckoutModal: FC<CheckoutModalProps> = ({
  isOpen,
  paymentMethod,
  receiptType,
  diningOption,
  total,
  isLoading = false,
  onClose,
  onConfirm
}) => {
  // Mapear diningOption a texto legible
  const getDiningOptionLabel = (option: string): string => {
    const labels: { [key: string]: string } = {
      'salon': 'Salón',
      'para-llevar': 'Llevar',
      'ir-comiendo': 'Ir Comiendo'
    };
    return labels[option] || option;
  };

  // Mapear receiptType a texto legible
  const getReceiptTypeLabel = (type: string): string => {
    const labels: { [key: string]: string } = {
      'boleta': 'Boleta',
      'boleta-dni': 'Boleta con DNI',
      'factura': 'Factura'
    };
    return labels[type] || type;
  };

  // Obtener color del badge de comprobante
  const getReceiptTypeBadgeColor = (type: string): string => {
    const colors: { [key: string]: string } = {
      'boleta': 'bg-green-600',
      'boleta-dni': 'bg-blue-600',
      'factura': 'bg-orange-600'
    };
    return colors[type] || 'bg-gray-600';
  };
  const [customerName, setCustomerName] = useState('');
  const [customerDocument, setCustomerDocument] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerBirthDate, setCustomerBirthDate] = useState('');
  const [isSearchingCustomer, setIsSearchingCustomer] = useState(false);
  const [customerFound, setCustomerFound] = useState(false);
  const [cashReceived, setCashReceived] = useState('');
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [textKeypadOpen, setTextKeypadOpen] = useState(false);
  const [dniKeypadOpen, setDniKeypadOpen] = useState(false);
  const [currentEditingField, setCurrentEditingField] = useState<{
    field: 'dni' | 'phone' | 'cash';
    title: string;
    maxLength?: number;
  } | null>(null);
  const [currentTextField, setCurrentTextField] = useState<{
    field: 'name' | 'email' | 'address';
    title: string;
    placeholder: string;
  } | null>(null);

  // Ref para evitar que el cierre del keypad cierre también el modal padre (race condition en responsive)
  const keypadJustClosedRef = useRef(false);

  const markKeypadClosed = () => {
    keypadJustClosedRef.current = true;
    setTimeout(() => { keypadJustClosedRef.current = false; }, 300);
  };


  const searchCustomer = async (document: string) => {
    if (document.length < 8) return;

    setIsSearchingCustomer(true);

    try {
      const result = await posService.searchCustomer(document);
      if (result.success && result.data) {
        // Cliente encontrado, rellenar campos
        const customer = result.data;
        setCustomerName(customer.name || '');
        setCustomerEmail(customer.email || '');
        setCustomerPhone(customer.phone || '');
        setCustomerAddress(customer.address || '');
        setCustomerBirthDate(customer.birthDate || '');
        setCustomerFound(true);
      } else {
        // Cliente no encontrado, limpiar campos excepto documento
        setCustomerName('');
        setCustomerEmail('');
        setCustomerPhone('');
        setCustomerAddress('');
        setCustomerBirthDate('');
        setCustomerFound(false);
      }
    } catch (error) {
      console.error('Error searching customer:', error);
      setCustomerFound(false);
    } finally {
      setIsSearchingCustomer(false);
    }
  };

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const document = e.target.value;
    setCustomerDocument(document);

    // Auto-buscar cuando el documento tenga 8 dígitos (DNI) o 11 dígitos (RUC)
    if (document.length === 8 || document.length === 11) {
      searchCustomer(document);
    } else {
      setCustomerFound(false);
    }
  };

  const openKeypadForDNI = () => {
    setDniKeypadOpen(true);
  };

  const openKeypadForPhone = () => {
    setCurrentEditingField({
      field: 'phone',
      title: 'Ingresa número de teléfono',
      maxLength: 15
    });
    setKeypadOpen(true);
  };

  const openKeypadForCash = () => {
    setCurrentEditingField({
      field: 'cash',
      title: 'Monto recibido'
    });
    setKeypadOpen(true);
  };

  const openTextKeypadForName = () => {
    setCurrentTextField({
      field: 'name',
      title: receiptType === 'factura' ? 'Nombre o Razón Social' : 'Nombre del cliente',
      placeholder: receiptType === 'factura' ? 'NOMBRE O RAZON SOCIAL' : 'NOMBRE DEL CLIENTE'
    });
    setTextKeypadOpen(true);
  };

  const openTextKeypadForEmail = () => {
    setCurrentTextField({
      field: 'email',
      title: 'Correo electrónico',
      placeholder: 'CORREO@EJEMPLO.COM'
    });
    setTextKeypadOpen(true);
  };

  const openTextKeypadForAddress = () => {
    setCurrentTextField({
      field: 'address',
      title: 'Dirección',
      placeholder: 'DIRECCION COMPLETA'
    });
    setTextKeypadOpen(true);
  };

  const handleKeypadSubmit = (value: number | string) => {
    if (!currentEditingField) return;

    if (currentEditingField.field === 'cash') {
      setCashReceived(value.toString());
    } else {
      const valueStr = value.toString();
      if (currentEditingField.field === 'dni') {
        setCustomerDocument(valueStr);
        // Auto-buscar después de ingresar
        if (valueStr.length === 8 || valueStr.length === 11) {
          searchCustomer(valueStr);
        }
      } else if (currentEditingField.field === 'phone') {
        setCustomerPhone(valueStr);
      }
    }
  };

  const handleTextKeypadSubmit = (value: string) => {
    if (!currentTextField) return;

    if (currentTextField.field === 'name') {
      setCustomerName(value);
    } else if (currentTextField.field === 'email') {
      setCustomerEmail(value);
    } else if (currentTextField.field === 'address') {
      setCustomerAddress(value);
    }
  };

  const handleDNIKeypadSubmit = (value: string) => {
    setCustomerDocument(value);
    // Auto-buscar después de ingresar
    if (value.length === 8 || value.length === 11) {
      searchCustomer(value);
    }
  };

  const getCurrentValue = () => {
    if (!currentEditingField) return 0;

    if (currentEditingField.field === 'dni') {
      return parseInt(customerDocument) || 0;
    } else if (currentEditingField.field === 'phone') {
      return parseInt(customerPhone) || 0;
    } else if (currentEditingField.field === 'cash') {
      return cashReceived || '0.00';
    }
    return 0;
  };

  // Calcular el vuelto
  const cashAmount = parseFloat(cashReceived) || 0;
  const changeAmount = cashAmount - total;
  const hasValidCashAmount = paymentMethod === 'efectivo' && cashAmount >= total;

  // Limpiar campos cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      setCustomerName('');
      setCustomerDocument('');
      setCustomerAddress('');
      setCustomerEmail('');
      setCustomerPhone('');
      setCustomerBirthDate('');
      setCustomerFound(false);
      setCashReceived('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    const cashAmount = parseFloat(cashReceived) || 0;
    const change = paymentMethod === 'efectivo' ? Math.max(0, cashAmount - total) : 0;

    onConfirm({
      paymentMethod,
      receiptType,
      customerName: customerName.trim() || undefined,
      customerDocument: customerDocument.trim() || undefined,
      customerAddress: receiptType === 'factura' ? customerAddress.trim() || undefined : undefined,
      customerEmail: customerEmail.trim() || undefined,
      customerPhone: customerPhone.trim() || undefined,
      customerBirthDate: customerBirthDate.trim() || undefined,
      cashReceived: paymentMethod === 'efectivo' ? cashAmount : total,
      changeAmount: change
    });
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const isFacturaValid = receiptType === 'boleta' ||
    (receiptType === 'boleta-dni' && customerDocument.trim() && customerName.trim()) ||
    (receiptType === 'factura' && customerName.trim() && customerDocument.trim() && customerAddress.trim());

  const isCashValid = paymentMethod !== 'efectivo' || hasValidCashAmount;

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'efectivo':
        return 'Efectivo';
      case 'tarjeta':
        return 'Tarjeta';
      case 'yape':
        return 'Yape/Plin';
      case 'plin':
        return 'Plin';
      case 'aplicativo':
        return 'Delivery';
      default:
        return 'Tarjeta';
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'efectivo':
        return Banknote;
      case 'yape':
      case 'plin':
        return Smartphone;
      case 'aplicativo':
        return Bike;
      case 'tarjeta':
      default:
        return CreditCard;
    }
  };

  const getPaymentMethodColor = (method: string) => {
    switch (method) {
      case 'efectivo':
        return 'bg-green-600';
      case 'yape':
        return 'bg-purple-600';
      case 'plin':
        return 'bg-blue-600';
      case 'aplicativo':
        return 'bg-orange-600';
      case 'tarjeta':
      default:
        return 'bg-blue-600';
    }
  };

  const paymentMethodLabel = getPaymentMethodLabel(paymentMethod);
  const PaymentIcon = getPaymentMethodIcon(paymentMethod);
  const paymentMethodColor = getPaymentMethodColor(paymentMethod);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
        // Evitar cierre accidental cuando hay un keypad abierto o se acaba de cerrar (race condition en responsive)
        if (!open && (keypadOpen || textKeypadOpen || dniKeypadOpen || keypadJustClosedRef.current)) return;
        if (!open) onClose();
      }}>
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="modal-overlay"
        onClick={handleOverlayClick}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PaymentIcon className="h-5 w-5 text-macartur-primary" />
            Confirmar Venta para {getDiningOptionLabel(diningOption)}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Resumen del pago */}
          <div className="text-center py-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Total a pagar</p>
            <p className="text-3xl font-bold text-macartur-primary">S/ {total.toFixed(2)}</p>

            {/* Método de pago y tipo de comprobante destacados */}
            <div className="flex flex-wrap items-center justify-center gap-3 mt-3">
              <div className="flex items-center gap-1">
                <span className="text-sm text-gray-600">Método:</span>
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-white font-medium text-sm ${paymentMethodColor}`}>
                  <PaymentIcon className="h-4 w-4" />
                  {paymentMethodLabel}
                </div>
              </div>

              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-white font-medium text-sm ${getReceiptTypeBadgeColor(receiptType)}`}>
                {getReceiptTypeLabel(receiptType)}
              </div>
            </div>
          </div>

          {/* Monto recibido y vuelto para efectivo */}
          {paymentMethod === 'efectivo' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
              <h3 className="font-medium text-green-800">Pago en Efectivo</h3>

              {/* Campo monto recibido */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-green-700">Monto recibido</label>
                <input
                  type="text"
                  placeholder="S/ 0.00"
                  value={cashReceived ? `S/ ${parseFloat(cashReceived).toFixed(2)}` : ''}
                  onClick={openKeypadForCash}
                  readOnly
                  className="w-full px-3 py-2 border border-green-300 rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              {/* Mostrar vuelto */}
              {cashReceived && (
                <div className="border-t border-green-200 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-green-700">Vuelto:</span>
                    <span className={`text-lg font-bold ${changeAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      S/ {Math.abs(changeAmount).toFixed(2)}
                    </span>
                  </div>
                  {changeAmount < 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      Falta: S/ {Math.abs(changeAmount).toFixed(2)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Datos del cliente */}
          {(receiptType === 'boleta' || receiptType === 'boleta-dni') && (
            <div className="space-y-3">
              <h3 className="font-medium">Datos del Cliente {receiptType === 'boleta-dni' ? '' : '(Opcional)'}</h3>

              {/* DNI primero con indicador de búsqueda */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="DNI"
                  value={customerDocument}
                  onClick={openKeypadForDNI}
                  readOnly
                  maxLength={8}
                  className={`w-full px-3 py-2 pr-10 border rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-macartur-primary ${
                    customerFound ? 'border-green-500 bg-green-50' : 'border-gray-300'
                  }`}
                />
                {isSearchingCustomer && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-macartur-primary"></div>
                  </div>
                )}
                {customerFound && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Search className="h-4 w-4 text-green-500" />
                  </div>
                )}
              </div>

              {customerFound && (
                <div className="text-sm text-green-600 bg-green-50 p-2 rounded-lg">
                  ✓ Cliente encontrado en la base de datos
                </div>
              )}

              <input
                type="text"
                placeholder="Nombre del cliente"
                value={customerName}
                onClick={openTextKeypadForName}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-macartur-primary"
              />
            </div>
          )}

          {receiptType === 'factura' && (
            <div className="space-y-3">
              <h3 className="font-medium">Datos del Cliente</h3>

              {/* RUC primero con indicador de búsqueda */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="RUC"
                  value={customerDocument}
                  onClick={openKeypadForDNI}
                  readOnly
                  maxLength={11}
                  className={`w-full px-3 py-2 pr-10 border rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-macartur-primary ${
                    customerFound ? 'border-green-500 bg-green-50' : 'border-gray-300'
                  }`}
                  required
                />
                {isSearchingCustomer && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-macartur-primary"></div>
                  </div>
                )}
                {customerFound && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Search className="h-4 w-4 text-green-500" />
                  </div>
                )}
              </div>

              {customerFound && (
                <div className="text-sm text-green-600 bg-green-50 p-2 rounded-lg">
                  ✓ Cliente encontrado en la base de datos
                </div>
              )}

              <input
                type="text"
                placeholder="Nombre o Razón Social"
                value={customerName}
                onClick={openTextKeypadForName}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-macartur-primary"
                required
              />
              <input
                type="text"
                placeholder="Dirección"
                value={customerAddress}
                onClick={openTextKeypadForAddress}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-macartur-primary"
                required
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-4">
          <Button variant="outline" onClick={onClose} disabled={isLoading} className="flex-1 max-w-[220px] py-6 text-lg font-semibold">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isFacturaValid || !isCashValid || isLoading}
            className="flex-1 max-w-[220px] py-6 text-lg font-semibold bg-macartur-primary hover:bg-macartur-primary/90"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Guardando...
              </>
            ) : (
              'Imprimir'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Numeric/Decimal Keypad Modal */}
      {currentEditingField && (
        currentEditingField.field === 'cash' ? (
          <DecimalKeypad
            isOpen={keypadOpen}
            onClose={() => {
              setKeypadOpen(false);
              setCurrentEditingField(null);
              markKeypadClosed();
            }}
            onSubmit={handleKeypadSubmit}
            title={currentEditingField.title}
            initialValue={getCurrentValue() as string}
          />
        ) : (
          <NumericKeypad
            isOpen={keypadOpen}
            onClose={() => {
              setKeypadOpen(false);
              setCurrentEditingField(null);
              markKeypadClosed();
            }}
            onSubmit={handleKeypadSubmit}
            title={currentEditingField.title}
            initialValue={getCurrentValue() as number}
          />
        )
      )}

      {/* Text Keypad Modal */}
      {currentTextField && (
        <TextKeypad
          isOpen={textKeypadOpen}
          onClose={() => {
            setTextKeypadOpen(false);
            setCurrentTextField(null);
            markKeypadClosed();
          }}
          onSubmit={handleTextKeypadSubmit}
          title={currentTextField.title}
          initialValue={
            currentTextField.field === 'name' ? customerName :
            currentTextField.field === 'email' ? customerEmail :
            currentTextField.field === 'address' ? customerAddress : ''
          }
          placeholder={currentTextField.placeholder}
        />
      )}

      <DNIKeypad
        isOpen={dniKeypadOpen}
        onClose={() => { setDniKeypadOpen(false); markKeypadClosed(); }}
        onSubmit={handleDNIKeypadSubmit}
        title={receiptType === 'factura' ? 'Ingresa RUC' : 'Ingresa DNI'}
        initialValue={customerDocument}
        maxLength={receiptType === 'factura' ? 11 : 8}
      />
    </Dialog>
  );
};
